import * as React from 'react';
import { ButtonPrimary, ElementLoader, ValidationProps } from 'ui';
import { SimpleFormComponentProps, SerializeType, SimpleFormProps } from 'app/infrastructure/simple-form/types';
import { FIELD_TYPE } from 'app/infrastructure/simple-form';
import { requiredValidation, validatePhoneNumber, SimpleFormValidationConfig } from 'app/infrastructure/simple-form/validation';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { form, countdownTimer, phoneNumberMinLength } from 'app/area/user-profile/my-notifications/constants';
import { Consent, Subscription, ConsentType } from 'app/area/user-profile/my-notifications/types';
import { SharedState } from 'app/types-shared';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';

import styles from './sms-modal.scss';

interface SmsModalProps {
  id?: number;
  recipientId: number;
  consent?: Consent;

  fetching: boolean;
  formComponent: React.ComponentType<SimpleFormComponentProps>;
  translation?: { [key: string]: string };
  validation: { [key: string]: ValidationProps };
  sharedState?: SharedState;

  onDone: () => void;
  onClose: () => void;
  verifyCode: (subscription: Subscription, onSuccess: () => void) => void;
  loadConsent?: (id: number, recipientId: number) => void;
  clearConsent?: () => void;
  clearValidation: (fieldName: string) => void;
}

interface SmsModalState {
  validPhoneNumber: boolean;
  counter: number;
}

export type SmsModalContainerProps = SmsModalProps & SimpleFormProps;

export class SmsModal extends React.PureComponent<SmsModalContainerProps, SmsModalState> {
  private interval: any;

  public state: Readonly<SmsModalState> = {
    validPhoneNumber: false,
    counter: countdownTimer.value
  };

  public componentDidUpdate(prevProps: SmsModalContainerProps, prevState: SmsModalState) {
    prevState.validPhoneNumber !== this.state.validPhoneNumber && this.counter();
  }

  public componentDidMount() {
    const { id, sharedState: { id: recipientId } } = this.props;

    this.setState({
      validPhoneNumber: false
    });

    this.clearValidation();

    if (id && recipientId) {
      this.props.clearConsent();
      this.props.loadConsent(id, recipientId);
    }
  }

  public componentWillUnmount() {
    this.interval && clearInterval(this.interval);
  }

  private counterOnDemand = () => {
    this.setState({ validPhoneNumber: true });

    if (this.state.counter === 0) {
      this.setState({
        counter: countdownTimer.value
      });
      this.counter();
    }
  };

  private onGenerateCode = () => {
    const { verifyCode, validate, serialize, sharedState: { id: recipientId }, translation } = this.props;

    const config: SimpleFormValidationConfig = [
      requiredValidation(form.sms.phoneNumber),
      validatePhoneNumber(form.sms.phoneNumber)
    ];

    const serializedForm = serialize(defaultSerializeMappings);
    const verificationMessage = translation.verification_code_message;
    const subscription = {
      value: {
        name: String(serializedForm.token),
        token: String(serializedForm.token)
      },
      type: ConsentType.sms,
      recipientId: Number(recipientId),
      ...((!!recipientId || recipientId === 0) && { id: recipientId }),
      ...(!!verificationMessage && { verificationMessage })
    };

    validate(config, () => verifyCode(subscription, () => {
      this.counterOnDemand();
      this.props.clearValidation(form.sms.phoneNumber);
    }));
  };

  private clearValidation = () => {
    if (!!this.props.validation) {
      this.props.clearValidation(form.sms.phoneNumber);
      this.props.clearValidation(form.sms.verificationCode);
    }
  };

  private isDisabled = () => {
    const { id, sharedState: { id: recipientId }, consent, fetching, formValues } = this.props;
    const { validPhoneNumber } = this.state;

    return (fetching || !validPhoneNumber)
      || (formValues && (!formValues[form.sms.verificationCode]
        || String(formValues[form.sms.verificationCode]).length < phoneNumberMinLength))
      || (id && recipientId && !(consent && consent.value));
  };

  private counter = () => {
    this.interval = setInterval(() => {
      this.setState({
        counter: this.state.counter - 1
      });
      this.state.counter <= 0 && clearInterval(this.interval);
    }, countdownTimer.intervalTime);
  };

  private clearPhoneNumberValidation = () => this.props.clearValidation(form.sms.phoneNumber);

  private clearVerificationCodeValidation = () => this.props.clearValidation(form.sms.verificationCode);

  public render() {
    const { id, sharedState: { id: recipientId }, consent, fetching, formComponent: SimpleForm, onDone, onClose, translation } = this.props;
    const { validPhoneNumber } = this.state;

    const countdown = validPhoneNumber && this.state.counter > 0 && `(${this.state.counter})`;

    return <ModalWrapper
      headerTitle={(id && recipientId) ? translation.change_phone_number : translation.add_sms_title}
      btnTitle={translation.save}
      btnCancelTitle={translation.cancel}
      onDone={onDone}
      onClose={onClose}
      largeSize={false}
      closeOnDone={false}
      isDisabled={this.isDisabled}
      canCloseOnBackground={false}>

      {id && recipientId && !(consent && consent.value) && fetching && <ElementLoader />}

      {(id && recipientId && consent && consent.value) || (!id && !consent)
        ? <>
          <p>{translation.phone_number_info}</p>

          {id && recipientId && <SimpleForm
            className={styles.readOnly}
            name={form.sms.previousPhoneNumber}
            type={FIELD_TYPE.TEXTAREA}
            label={{ text: translation.previous_phone_number }}
            initialValue={consent.value.token}
            readOnly={true}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }} />}

          <div className={styles.wrapper}>
            <div className={styles.input}>
              <SimpleForm
                name={form.sms.phoneNumber}
                type={FIELD_TYPE.TEXTAREA}
                label={{ text: translation.new_phone_number, required: true }}
                initialValue={''}
                maxLength={254}
                clearServerValidation={this.clearPhoneNumberValidation}
                serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
                readOnly={validPhoneNumber} />
            </div>
            <div className={styles.right}>
              <ButtonPrimary onClick={this.onGenerateCode} disabled={fetching || (validPhoneNumber && this.state.counter > 0)}>
                {translation.send_code} {countdown}
              </ButtonPrimary>
            </div>
          </div>
          {validPhoneNumber && <>
            <p>{translation.code_info}</p>
            <div className="row">
              <div className="col-6">
                <SimpleForm
                  name={form.sms.verificationCode}
                  type={FIELD_TYPE.TEXTAREA}
                  label={{ text: translation.verification_code, required: true }}
                  initialValue={''}
                  maxLength={10}
                  clearServerValidation={this.clearVerificationCodeValidation}
                  serialize={{ type: SerializeType.PRIMITIVE_VALUE }} />
              </div>
            </div>
          </>}
        </> : null}
    </ModalWrapper>;
  }
}
